import React from 'react';
import { Briefcase, HelpCircle } from 'lucide-react';

const EntrepreneurSection = ({ formData, handleInputChange, errors }) => {
  // Если статус ИП не выбран, возвращаем только checkbox
  if (!formData.isIp) {
    return (
      <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <label htmlFor="isIp" className="flex items-center cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
            <Briefcase className="w-5 h-5 mr-2 text-indigo-500" />
            <span>Индивидуальный предприниматель (ИП)</span>
          </label>
          <div>
            <input
              type="checkbox"
              id="isIp"
              name="isIp"
              checked={formData.isIp}
              onChange={handleInputChange}
              className="form-checkbox"
              disabled={formData.isSelfEmployed}
            />
          </div>
        </div>
        {formData.isSelfEmployed && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Нельзя одновременно быть ИП и самозанятым с одним видом деятельности.
          </p>
        )}
      </div>
    );
  }

  // Если выбран статус ИП, показываем дополнительные параметры
  return (
    <div className="mb-6 bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-100 dark:border-orange-800">
      <div className="flex items-center justify-between mb-4">
        <label htmlFor="isIp" className="flex items-center cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
          <Briefcase className="w-5 h-5 mr-2 text-orange-500" />
          <span>Индивидуальный предприниматель (ИП)</span>
        </label>
        <div>
          <input
            type="checkbox"
            id="isIp"
            name="isIp"
            checked={formData.isIp}
            onChange={handleInputChange}
            className="form-checkbox"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Система налогообложения */}
        <div className="col-span-full">
          <label htmlFor="ipTaxSystem" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Система налогообложения
          </label>
          <select
            id="ipTaxSystem"
            name="ipTaxSystem"
            value={formData.ipTaxSystem}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.ipTaxSystem ? 'input-error' : ''}`}
          >
            <option value="osn">Общая система налогообложения (ОСНО)</option>
            <option value="usn_income">УСН "Доходы" (6%)</option>
            <option value="usn_income_expenses">УСН "Доходы минус расходы" (15%)</option>
            <option value="eshn">Единый сельскохозяйственный налог (ЕСХН)</option>
            <option value="psn">Патентная система налогообложения (ПСН)</option>
            <option value="patent">Патент</option>
          </select>
          {errors.ipTaxSystem && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.ipTaxSystem}
            </p>
          )}
        </div>

        {/* Расходы (для УСН "Доходы минус расходы") */}
        {formData.ipTaxSystem === 'usn_income_expenses' && (
          <div className="col-span-full">
            <label htmlFor="ipExpenses" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Расходы
            </label>
            <div className="relative">
              <input
                type="text"
                id="ipExpenses"
                name="ipExpenses"
                value={formData.ipExpenses}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.ipExpenses ? 'input-error' : ''}`}
                placeholder="Введите сумму расходов"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 dark:text-gray-400">₽</span>
              </div>
            </div>
            {errors.ipExpenses && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.ipExpenses}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Укажите документально подтвержденные расходы, связанные с предпринимательской деятельностью
            </p>
          </div>
        )}

        {/* Информационный блок */}
        <div className="col-span-full mt-2">
          <div className="flex items-start bg-orange-100 dark:bg-orange-900/30 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
            <HelpCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-1">
                Особенности налогообложения ИП
              </h4>
              <p className="text-xs text-orange-600 dark:text-orange-400">
                {formData.ipTaxSystem === 'osn' && 'На ОСНО вы уплачиваете НДФЛ по ставке 13% с доходов за вычетом расходов. Также учтите, что на ОСНО нужно платить НДС и налог на имущество, используемое в предпринимательской деятельности.'}
                {formData.ipTaxSystem === 'usn_income' && 'На УСН "Доходы" вы уплачиваете 6% с доходов. Налог можно уменьшить до 50% на сумму уплаченных страховых взносов.'}
                {formData.ipTaxSystem === 'usn_income_expenses' && 'На УСН "Доходы минус расходы" вы уплачиваете 15% с разницы между доходами и расходами. Минимальный налог – 1% от доходов, который нужно заплатить, если сумма исчисленного налога меньше.'}
                {formData.ipTaxSystem === 'eshn' && 'ЕСХН применяют сельхозпроизводители. Ставка налога – 6% от разницы между доходами и расходами. Для применения ЕСХН доля выручки от сельхозпродукции должна быть не менее 70%.'}
                {formData.ipTaxSystem === 'psn' && 'ПСН может применяться только для определенных видов деятельности. Налог рассчитывается как 6% от потенциально возможного годового дохода, который устанавливается региональными законами.'}
                {formData.ipTaxSystem === 'patent' && 'Патент можно приобрести на срок от 1 до 12 месяцев. Стоимость патента – 6% от потенциально возможного годового дохода. Патент действует только на территории региона, где он приобретен.'}
              </p>
              <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
                Помимо налогов, не забывайте об обязательных страховых взносах "за себя": фиксированные платежи на пенсионное и медицинское страхование, плюс 1% с доходов свыше 300 000 ₽.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntrepreneurSection;